/**
 * PendingInviteGate — keeps a mid-acceptance invitee on the guided path
 * (task #45, 2026-08-10). The member-side sibling of TrialOnboardingGate.
 *
 * While a pending team invitation is recorded (see src/lib/pendingInvite.ts),
 * any signed-in visit to an app surface — including the chat front door —
 * redirects back to the acceptance page, where the invite auto-accepts and
 * clears the marker. Closing the browser mid-journey and coming back later
 * resumes exactly where the invitee left off; there is nothing to re-find
 * in their inbox.
 *
 * Never a trap: the accept page clears the marker on EVERY terminal outcome
 * (accepted, already-member, expired, invalid, hard error), so at most one
 * bounce ever happens before the state resolves.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isGatedAppPath } from '../../hooks/trialOnboardingGate';
import { getPendingInvite } from '../../lib/pendingInvite';

export const PendingInviteGate: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) return null;

  const path = location.pathname.toLowerCase();
  // Never interfere with the acceptance page itself or the auth stopover.
  if (path.startsWith('/accept-invite') || path.startsWith('/auth')) return null;
  // Gate the app surfaces the trial gate covers PLUS the chat front door —
  // acceptance lands on '/', so an unresolved invite must not idle there.
  if (!isGatedAppPath(location.pathname) && path !== '/') return null;

  const pending = getPendingInvite();
  if (!pending) return null;

  return <Navigate to={pending.url} replace />;
};

export default PendingInviteGate;
